import React, {useEffect, useState} from "react";
import {useFormik} from "formik";
import * as Yup from "yup";
import {CONFIG} from "../../../config/mainConfig";
import SubmitButton from "../../../components/Common/SubmitButton";
import { handleCouponSubmitService } from "./services/AdminForms.service";
import { CouponDurationEnum, CouponTypesEnum } from "../../../interfaces/coupon.interface";

interface CouponFormProps {
    setShowForm: (value: boolean) => void;
    getCoupons: () => void;
    couponDetails: any;
}

export const CouponForm = ({setShowForm, getCoupons, couponDetails}: CouponFormProps) => {
    const [errors, setErrors] = useState({});
    const [loader, setLoader] = useState(false);
    const validationSchema = (Yup.object() as any)
        .shape({
            couponCode: Yup.string().required("Coupon Code is required"),
            couponType: Yup.string().required("Coupon Type is required"),
            couponAmount: Yup.string().required("Coupon Amount / Percentage is required"),
            couponAppliedFor: Yup.string().required("Coupon For is required"),
            couponExpiry: Yup.string().required("Coupon Expiry is required"),
        })
        .when(["couponValidity", "couponValidForMonths"], {
            is: (couponValidity: string, couponValidForMonths: string) => couponValidity === CouponDurationEnum.REPEATING,
            then: Yup.object({
                couponValidForMonths: Yup.string().required("Number of months is required"),
            }),
        });

    const formik = useFormik({
        initialValues: {
            couponId: "",
            couponCode: "",
            couponType: "",
            couponAmount: "",
            couponAppliedFor: "",
            couponExpiry: "",
            couponValidity: "",
            couponValidForMonths: "",
            couponDescription: "",
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            handleSubmit();
        },
    });

    const handleSubmit = async () => {
        await handleCouponSubmitService(formik.values, setLoader, getCoupons, setShowForm);
    };
      
    useEffect(() => {
        if (couponDetails) {
            const values: React.SetStateAction<any> = {
                couponId: couponDetails.couponId,
                couponCode: couponDetails?.data?.couponCode,
                couponType: couponDetails?.data?.couponType,
                couponAmount: couponDetails?.data?.couponAmount,
                couponAppliedFor: couponDetails?.data?.couponAppliedFor,
                couponExpiry: couponDetails?.data?.couponExpiry,
                couponValidity: couponDetails?.data?.couponValidity,
                couponValidForMonths: couponDetails?.data?.couponValidForMonths,
            };
            formik.setValues(values);
        }
    }, [couponDetails]);
    return (
        <div className="bg-storyta mx-auto">
            <div className="max-w-7xl mx-auto min-h-screen">
                {couponDetails ? (
                    <h1 className="text-storyblue text-xl font-bold text-start mb-4">Edit Coupon</h1>
                ) : (
                    <h1 className="text-storyblue text-xl font-bold text-start mb-4">New Coupon</h1>
                )}
                <form id="form-signup" name="form-signup" className="text-start " onSubmit={formik.handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">Coupon Code</label>
                            <input
                                type="text"
                                name="couponCode"
                                value={formik.values.couponCode}
                                onChange={formik.handleChange}
                                placeholder="Coupon Code"
                                className="p-2 w-full rounded-md uppercase"
                                autoFocus={true}
                            />
                            <div className="text-red-600 mt-1">{formik.errors.couponCode}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">Select Coupon Type</label>
                            <select
                                name="couponType"
                                value={formik.values.couponType}
                                onChange={formik.handleChange}
                                className="p-2 w-full rounded-md"
                            >
                                <option value="" disabled>
                                    Select Coupon Type
                                </option>
                                <option value="fixed">Fixed</option>
                                <option value="percentage">Percentage</option>
                            </select>
                            <div className="text-red-600 mt-1">{formik.errors.couponType}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">Coupon Amount / Percentage</label>
                            <input
                                type="text"
                                name="couponAmount"
                                value={formik.values.couponAmount}
                                onChange={formik.handleChange}
                                placeholder="Coupon Amount / Percentage"
                                className="p-2 w-full rounded-md"
                            />
                            <div className="text-red-600 mt-1">{formik.errors.couponAmount}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">Select Coupon Applied For</label>
                            <select
                                name="couponAppliedFor"
                                value={formik.values.couponAppliedFor}
                                onChange={formik.handleChange}
                                className="p-2 w-full rounded-md"
                            >
                                <option value="" disabled>
                                    Select Coupon Applied For
                                </option>
                                <option value={CouponTypesEnum.FOR_BOOK_PURCHASE}>Book Purchase</option>
                                <option value={CouponTypesEnum.FOR_PLAN_PURCHASE}>Plan Purchase</option>
                                <option value={CouponTypesEnum.FOR_CREDITS_PURCHASE}>Credits Purchase</option>
                            </select>
                            <div className="text-red-600 mt-1">{formik.errors.couponAppliedFor}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">Coupon Expiry Date</label>
                            <input
                                type="date"
                                min={new Date().toISOString().split("T")[0]}
                                name="couponExpiry"
                                value={formik.values.couponExpiry}
                                onChange={formik.handleChange}
                                placeholder="Coupon Expiry"
                                className="p-2 w-full rounded-md"
                                readOnly={couponDetails ? true : false}
                            />
                            <div className="text-red-600 mt-1">{formik.errors.couponExpiry}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">Select Duration</label>
                            <select
                                name="couponValidity"
                                value={formik.values.couponValidity}
                                onChange={formik.handleChange}
                                className="p-2 w-full rounded-md"
                            >
                                <option value="" disabled>
                                    Select Duration
                                </option>
                                <option value={CouponDurationEnum.ONCE}>Once</option>
                                <option value={CouponDurationEnum.FOREVER}>Unlimited</option>
                            </select>
                            <div className="text-red-600 mt-1">{formik.errors.couponValidity}</div>
                        </div>
                        <div className="flex flex-col gap-2 mt-1">
                            <label className="font-medium">Coupon Description</label>
                            <textarea
                                name="couponDescription"
                                value={formik.values.couponDescription}
                                onChange={formik.handleChange}
                                className="p-2 w-full rounded-md"
                                rows={2}
                                maxLength={100}
                                placeholder="Enter coupon description"
                            />
                            <div className="text-red-600 mt-1">{formik.errors.couponDescription}</div>
                        </div>
                        {formik.values.couponValidity === CouponDurationEnum.REPEATING && (
                            <div>
                                <label className="font-medium">Number of Months</label>
                                <select
                                    name="couponValidForMonths"
                                    value={formik.values.couponValidForMonths}
                                    onChange={formik.handleChange}
                                    className="p-2 w-full rounded-md"
                                >
                                    <option value="">Select Number of Months</option>
                                    {[...Array(12)].map((_, index) => (
                                        <option key={index + 1} value={index + 1}>
                                            {index + 1} Month{index !== 0 ? "s" : ""}
                                        </option>
                                    ))}
                                </select>
                                <div className="text-red-600 mt-1">{formik.errors.couponValidForMonths}</div>
                            </div>
                        )}
                    </div>
                    <SubmitButton title="Save" loader={loader} cancelOperation={setShowForm}/>
                </form>
            </div>
        </div>
    );
};
